import axios from 'axios';

const TOKEN_KEY = 'tnm_crm_token';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach token from localStorage on every request.
// This ensures the token is always current, even if it was updated
// by another tab or after a re-login.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Guard against multiple concurrent 401 redirects triggering simultaneously.
let isRedirectingToLogin = false;

// Response interceptor: handle 401 (session expired / invalid token).
// Since the backend has no refresh-token endpoint, the only recovery
// is to clear the stale token and send the user to /login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl: string = error.config?.url ?? '';

      // Never intercept auth endpoints — let the caller (AuthContext)
      // handle login failures and /auth/me 401s directly.
      const isAuthEndpoint =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/me') ||
        requestUrl.includes('/auth/logout');

      const isOnLoginPage = window.location.pathname === '/login';

      if (!isAuthEndpoint && !isOnLoginPage && !isRedirectingToLogin) {
        isRedirectingToLogin = true;

        // Clear stale credentials
        localStorage.removeItem(TOKEN_KEY);
        delete api.defaults.headers.common['Authorization'];

        // Redirect to login — use replace so the expired page doesn't
        // stay in the browser history.
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
export type PreferredPhone = 'primary' | 'mobile' | 'home' | 'work';

export interface Contact {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  // Phone numbers
  primary_phone: string | null;
  mobile_phone: string | null;
  home_phone: string | null;
  work_phone: string | null;
  preferred_phone: PreferredPhone;
  // Primary Address (main residence/mailing - used for county assignment)
  primary_address_line1: string | null;
  primary_address_line2: string | null;
  primary_city: string | null;
  primary_state: string;
  primary_zip: string | null;
  primary_county: string | null;
  // Voting Address (where registered to vote)
  voting_address_line1: string | null;
  voting_address_line2: string | null;
  voting_city: string | null;
  voting_state: string | null;
  voting_zip: string | null;
  voting_county: string | null;
  voting_precinct: string | null;
  voting_differs_from_primary: boolean;
  // Shipping Address (for merchandise/materials)
  shipping_address_line1: string | null;
  shipping_address_line2: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_zip: string | null;
  shipping_differs_from_primary: boolean;
  // Billing Address (for payment processing)
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_city: string | null;
  billing_state: string;
  billing_zip: string | null;
  billing_differs_from_primary: boolean;
  // Primary county for team assignment (derived from primary_county)
  county: string | null;
  category: string;
  membership_status: string | null;
  membership_tier: string | null;
  engagement_score: number;
  lifetime_donations: number;
  declaration_status: string;
  is_member: boolean;
  is_donor: boolean;
  is_volunteer: boolean;
  is_declared: boolean;
  is_archived?: boolean;
  archived_at?: string | null;
  archive_reason?: string | null;
  // Integration/source tracking
  original_source: string | null;
  original_campaign: string | null;
  external_ids: Record<string, string | number> | null;
  // Important dates
  birthdate: string | null;
  anniversary: string | null;
  supporter_since: string | null;
  member_since: string | null;
  declaration_date: string | null;
  membership_expiration: string | null;
  voter_registration_date: string | null;
  blueprint_completed_date: string | null;
  first_donation_date: string | null;
  last_donation_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

/** Wrapper for API responses that return { data: T } */
export interface DataResponse<T> {
  data: T;
}

export interface DashboardStats {
  total_supporters: number;
  total_members: number;
  total_declared: number;
  total_donors: number;
  total_donations: number;
  declaration_goal: number;
  declaration_percentage: number;
  new_supporters_30d: number;
  new_members_30d: number;
  new_declared_30d: number;
}

// TNM Universe types
export type ContactCategory = 'texan' | 'voter' | 'lead' | 'supporter' | 'member' | 'declared_texian' | 'leader';
export type MembershipTier = 'texian' | 'partisan' | 'founder' | 'lifetime' | 'premium' | 'participating' | 'monthly' | 'annual' | null;
export type EngagementRecency = 'hot' | 'warm' | 'cold' | 'dormant';

export interface TnmUniverseSummary {
  total_contacts: number;
  registered_voters: number;
  supporters: number;
  members: number;
  declared_texians: number;
  leaders: number;
  hot_contacts: number;
  warm_contacts: number;
  cold_contacts: number;
  dormant_contacts: number;
  // member.tnm.me tiers
  texian_members: number;
  partisan_members: number;
  founder_members: number;
  lifetime_members: number;
  // tnm.me legacy tiers
  premium_members: number;
  participating_members: number;
}

export interface SupporterPathwayStats {
  pathway: string;
  count: number;
  converted_to_member: number;
  declared: number;
  avg_engagement: number;
  avg_donations: number;
}

export interface EngagementRecencyStats {
  hot: { count: number; percentage: number };
  warm: { count: number; percentage: number };
  cold: { count: number; percentage: number };
  dormant: { count: number; percentage: number };
  total: number;
}

export interface MembershipRevenueStats {
  texian: { count: number; monthly_revenue: number };
  partisan: { count: number; monthly_revenue: number };
  founder: { count: number; monthly_revenue: number };
  lifetime: { count: number; total_value: number };
  total_mrr: number;
}

// Membership tier pricing constants
export const MEMBERSHIP_TIERS = {
  texian: { name: 'Texian', price: 4.21, frequency: 'monthly' },
  partisan: { name: 'Partisan', price: 18.36, frequency: 'monthly' },
  founder: { name: 'Founder', price: 36.00, frequency: 'monthly' },
  lifetime: { name: 'Lifetime', price: 500.00, frequency: 'one-time' },
} as const;

// Category display names
export const CATEGORY_LABELS: Record<ContactCategory, string> = {
  texan: 'Texan',
  voter: 'Voter',
  lead: 'Lead',
  supporter: 'Supporter',
  member: 'Member',
  declared_texian: 'Declared Texian',
  leader: 'Leader',
};

// Engagement recency labels and colors
export const RECENCY_CONFIG: Record<EngagementRecency, { label: string; color: string; bgColor: string }> = {
  hot: { label: 'Hot', color: 'text-red-700', bgColor: 'bg-red-100' },
  warm: { label: 'Warm', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  cold: { label: 'Cold', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  dormant: { label: 'Dormant', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

export interface DeclarationCount {
  count: number;
  goal: number;
  percentage: number;
  last_updated: string | null;
}

// Analytics response types
export interface FunnelStats {
  leads: number;
  supporters: number;
  members: number;
  declared: number;
  leaders: number;
  conversion_rates: {
    lead_to_supporter: number;
    supporter_to_member: number;
    member_to_declared: number;
  };
}

export interface MembershipStats {
  active_members: number;
  by_tier: Record<string, number>;
  retention_rate: number;
  monthly_revenue: number;
}

export interface EngagementStats {
  avg_engagement: number;
  high_engagement_count: number;
  medium_engagement_count: number;
  low_engagement_count: number;
}

// Declaration public types
export interface DeclarationLeaderboardEntry {
  declaration_number: number;
  first_name: string;
  county: string;
  counted_at: string;
}

export interface RecentDeclarationEntry {
  declaration_number: number;
  first_name: string;
  county: string;
  counted_at: string;
}

// County stats type
export interface CountyStat {
  id: string;
  county_name: string;
  status: string;
  total_contacts: number;
  total_members: number;
  total_declared: number;
  total_donations: number;
  avg_engagement: number;
}

// API functions
export const contactsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Contact>>('/contacts', { params }),
  get: (id: string) => api.get<Contact>(`/contacts/${id}`),
  create: (data: Partial<Contact>) => api.post<Contact>('/contacts', data),
  update: (id: string, data: Partial<Contact>) => api.patch<Contact>(`/contacts/${id}`, data),
  delete: (id: string) => api.delete(`/contacts/${id}`),
  search: (params: Record<string, unknown>) =>
    api.post<PaginatedResponse<Contact>>('/contacts/search', params),
  archive: (id: string, reason?: string) =>
    api.post<Contact>(`/contacts/${id}/archive`, { reason }),
  restore: (id: string) =>
    api.post<Contact>(`/contacts/${id}/restore`),
  bulkArchive: (ids: string[], reason?: string) =>
    api.post<{ archived: number }>('/contacts/bulk/archive', { ids, reason }),
  bulkDelete: (ids: string[]) =>
    api.post<{ deleted: number }>('/contacts/bulk/delete', { ids }),
};

export interface DonationReport {
  total_amount: number;
  donation_count: number;
  avg_donation: number;
  recurring_count: number;
  one_time_count: number;
  new_donors: number;
  by_platform: Array<{ platform: string; amount: number; count: number }>;
  by_campaign: Array<{ campaign: string; amount: number; count: number }>;
  daily_totals: Array<{ date: string; amount: number; count: number }>;
  monthly_totals: Array<{ month: string; amount: number; count: number }>;
}

export const analyticsApi = {
  dashboard: () => api.get<DataResponse<DashboardStats>>('/analytics/dashboard'),
  engagement: (days?: number) => api.get<DataResponse<EngagementStats>>('/analytics/engagement', { params: { days } }),
  funnel: () => api.get<DataResponse<FunnelStats>>('/analytics/funnel'),
  donations: (params?: { days?: number; start_date?: string; end_date?: string }) =>
    api.get<DataResponse<DonationReport>>('/analytics/donations', { params }),
  memberships: () => api.get<DataResponse<MembershipStats>>('/analytics/memberships'),
  counties: () => api.get<DataResponse<unknown>>('/analytics/counties'),
  // TNM Universe endpoints
  universe: () => api.get<DataResponse<TnmUniverseSummary>>('/analytics/universe'),
  pathways: () => api.get<DataResponse<SupporterPathwayStats[]>>('/analytics/pathways'),
  recency: () => api.get<DataResponse<EngagementRecencyStats>>('/analytics/recency'),
  revenue: () => api.get<DataResponse<MembershipRevenueStats>>('/analytics/revenue'),
};

export const declarationsApi = {
  count: () => api.get<DataResponse<DeclarationCount>>('/declarations/count'),
  leaderboard: (limit?: number) => api.get<DataResponse<DeclarationLeaderboardEntry[]>>('/declarations/leaderboard', { params: { limit } }),
  recent: (limit?: number) => api.get<DataResponse<RecentDeclarationEntry[]>>('/declarations/recent', { params: { limit } }),
  byStatus: (status: string) => api.get<DataResponse<unknown[]>>(`/declarations/by-status/${status}`),
};

export const countiesApi = {
  list: () => api.get<DataResponse<CountyStat[]>>('/counties'),
  get: (name: string) => api.get(`/counties/${encodeURIComponent(name)}`),
  contacts: (name: string, params?: Record<string, unknown>) =>
    api.get(`/counties/${encodeURIComponent(name)}/contacts`, { params }),
  metrics: (name: string, days?: number) =>
    api.get<DataResponse<unknown[]>>(`/counties/${encodeURIComponent(name)}/metrics`, { params: { days } }),
};

export const usersApi = {
  list: (params?: Record<string, unknown>) => api.get('/users', { params }),
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: Record<string, unknown>) => api.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

export const editRequestsApi = {
  list: (params?: Record<string, unknown>) => api.get('/edit-requests', { params }),
  get: (id: string) => api.get(`/edit-requests/${id}`),
  create: (data: Record<string, unknown>) => api.post('/edit-requests', data),
  review: (id: string, data: { status: string; review_notes?: string }) =>
    api.patch(`/edit-requests/${id}/review`, data),
  pendingCount: () => api.get<{ pending_count: number }>('/edit-requests/pending/count'),
};

// Tag types
export interface Tag {
  id: string;
  name: string;
  slug: string;
  color: string;
  description: string | null;
  is_system: boolean;
  contact_count?: number;
  created_at: string;
  updated_at: string;
}

export const tagsApi = {
  list: () => api.get<{ data: Tag[] }>('/tags'),
  get: (id: string) => api.get<{ data: Tag }>(`/tags/${id}`),
  create: (data: { name: string; color?: string; description?: string }) =>
    api.post<{ data: Tag }>('/tags', data),
  update: (id: string, data: { name?: string; color?: string; description?: string }) =>
    api.patch<{ data: Tag }>(`/tags/${id}`, data),
  delete: (id: string) => api.delete(`/tags/${id}`),
  bulk: (data: { contact_ids: string[]; tag_ids: string[]; action: 'add' | 'remove' }) =>
    api.post('/tags/bulk', data),
};

// Contact tags
export const contactTagsApi = {
  get: (contactId: string) => api.get<{ data: Tag[] }>(`/contacts/${contactId}/tags`),
  add: (contactId: string, tagIds: string[]) =>
    api.post<{ data: Tag[] }>(`/contacts/${contactId}/tags`, { tag_ids: tagIds }),
  remove: (contactId: string, tagId: string) =>
    api.delete(`/contacts/${contactId}/tags/${tagId}`),
  set: (contactId: string, tagIds: string[]) =>
    api.put<{ data: Tag[] }>(`/contacts/${contactId}/tags`, { tag_ids: tagIds }),
};

// Tag Rule types for smart tagging
export type TagRuleOperator =
  | 'equals' | 'not_equals'
  | 'contains' | 'not_contains'
  | 'starts_with' | 'ends_with'
  | 'greater_than' | 'less_than'
  | 'greater_than_or_equal' | 'less_than_or_equal'
  | 'is_true' | 'is_false'
  | 'is_null' | 'is_not_null'
  | 'in_list' | 'not_in_list'
  | 'days_ago_less_than' | 'days_ago_greater_than';

export interface TagRuleCondition {
  field: string;
  operator: TagRuleOperator;
  value: string | number | boolean | string[] | null;
}

export interface TagRule {
  id: string;
  tag_id: string;
  name: string;
  description: string | null;
  conditions: TagRuleCondition[];
  match_type: 'all' | 'any';
  is_active: boolean;
  priority: number;
  last_run_at: string | null;
  last_run_affected: number;
  created_at: string;
  updated_at: string;
}

export interface TagRuleField {
  field: string;
  label: string;
  type: 'boolean' | 'number' | 'string' | 'date';
}

export interface TagRulePreviewResult {
  count: number;
  sample: Array<{ id: string; email: string; first_name: string | null; last_name: string | null }>;
}

export const tagRulesApi = {
  list: () => api.get<{ data: TagRule[] }>('/tag-rules'),
  get: (id: string) => api.get<{ data: TagRule }>(`/tag-rules/${id}`),
  getFields: () => api.get<{ data: TagRuleField[] }>('/tag-rules/fields'),
  create: (data: {
    tag_id: string;
    name: string;
    description?: string;
    conditions: TagRuleCondition[];
    match_type?: 'all' | 'any';
    is_active?: boolean;
    priority?: number;
  }) => api.post<{ data: TagRule }>('/tag-rules', data),
  update: (id: string, data: {
    name?: string;
    description?: string;
    conditions?: TagRuleCondition[];
    match_type?: 'all' | 'any';
    is_active?: boolean;
    priority?: number;
  }) => api.patch<{ data: TagRule }>(`/tag-rules/${id}`, data),
  delete: (id: string) => api.delete(`/tag-rules/${id}`),
  preview: (data: { conditions: TagRuleCondition[]; match_type: 'all' | 'any' }) =>
    api.post<{ data: TagRulePreviewResult }>('/tag-rules/preview', data),
  run: (id: string) => api.post<{ success: boolean; message: string; affected: number }>(`/tag-rules/${id}/run`),
  runAll: () => api.post<{ success: boolean; message: string; rules_run: number; contacts_affected: number }>('/tag-rules/run-all'),
};

// Donation types
export interface Donation {
  id: string;
  contact_id: string;
  contact_email: string;
  contact_name: string;
  amount: number;
  currency: string;
  donation_date: string;
  source_platform: string | null;
  source_campaign: string | null;
  campaign_name: string | null;
  donation_type: string | null;
  recurring_frequency: string | null;
  designation: string | null;
  payment_method: string | null;
  status: string;
  created_at: string;
}

export interface DonationSummary {
  total_amount: number;
  donation_count: number;
  avg_amount: number;
  recurring_count: number;
  unique_donors: number;
  period_days: number;
}

export const donationsApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Donation>>('/donations', { params }),
  get: (id: string) => api.get<Donation>(`/donations/${id}`),
  create: (data: Record<string, unknown>) => api.post<Donation>('/donations', data),
  update: (id: string, data: Record<string, unknown>) => api.patch<Donation>(`/donations/${id}`, data),
  summary: (params?: { days?: number; start_date?: string; end_date?: string; source_platform?: string; donation_type?: string }) =>
    api.get<DataResponse<DonationSummary>>('/donations/stats/summary', { params }),
};

// Event types
export interface Event {
  id: string;
  title: string;
  description: string | null;
  event_type: string;
  start_date: string;
  end_date: string | null;
  all_day: boolean;
  location: string | null;
  address: string | null;
  county: string | null;
  county_id: string | null;
  county_name?: string | null;
  location_lat: number | null;
  location_lng: number | null;
  max_attendees: number | null;
  requires_rsvp: boolean;
  is_public: boolean;
  is_cancelled: boolean;
  status: 'draft' | 'published' | 'cancelled' | 'completed';
  organizer_id: string | null;
  organizer_name?: string;
  rsvp_count?: number;
  registration_count?: number;
  attended_count?: number;
  declarations_count?: number;
  created_at: string;
  updated_at: string;
}

export interface EventRegistration {
  id: string;
  event_id: string;
  contact_id: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string | null;
  contact_county?: string | null;
  declaration_status?: string;
  status: 'registered' | 'confirmed' | 'attended' | 'no_show' | 'cancelled';
  registered_at: string;
  confirmed_at: string | null;
  attended_at: string | null;
  checked_in_by: string | null;
  notes: string | null;
  headcount: number;
}

export interface RegistrationSummary {
  registered: number;
  confirmed: number;
  attended: number;
  no_show: number;
  cancelled: number;
  total: number;
}

// Legacy support
export interface EventRsvp {
  id: string;
  event_id: string;
  contact_id: string;
  contact_name: string;
  contact_email: string;
  status: string;
  headcount: number;
  notes: string | null;
  responded_at: string | null;
}

export interface RsvpSummary {
  attending: number;
  not_attending: number;
  maybe: number;
  pending: number;
  total_headcount: number;
}

export interface EventStats {
  total_events: number;
  ceremonies: number;
  total_registrations: number;
  total_attended: number;
  declarations_from_ceremonies: number;
  attendance_rate: number;
  declaration_rate: number;
  upcoming_count: number;
  this_month_count: number;
}

export interface CeremonyDeclaration {
  contact_id: string;
  first_name: string;
  last_name: string;
  email: string;
  county: string;
  house_district: string;
  declaration_date: string;
}

export const eventsApi = {
  // List & search
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Event>>('/events', { params }),
  upcoming: (limit?: number) => api.get<DataResponse<Event[]>>('/events/upcoming', { params: { limit } }),
  calendar: (year: number, month: number) =>
    api.get<DataResponse<Event[]>>('/events/calendar', { params: { year, month } }),
  stats: (days?: number) => api.get<EventStats>('/events/stats', { params: { days } }),

  // Single event
  get: (id: string) => api.get<Event & {
    rsvp_summary: RsvpSummary;
    registration_summary: RegistrationSummary;
    declarations_count?: number;
  }>(`/events/${id}`),
  create: (data: Record<string, unknown>) => api.post<Event>('/events', data),
  update: (id: string, data: Record<string, unknown>) => api.patch<Event>(`/events/${id}`, data),
  delete: (id: string) => api.delete(`/events/${id}`),
  cancel: (id: string) => api.post<Event>(`/events/${id}/cancel`),
  complete: (id: string) => api.post<Event>(`/events/${id}/complete`),

  // Registrations
  getRegistrations: (id: string) => api.get<{
    registrations: EventRegistration[];
    summary: RegistrationSummary;
    rsvps: EventRsvp[]; // Legacy
  }>(`/events/${id}/registrations`),
  register: (id: string, data: { contact_id: string; status?: string; notes?: string }) =>
    api.post<EventRegistration>(`/events/${id}/register`, data),
  updateRegistration: (eventId: string, regId: string, data: { status: string; notes?: string }) =>
    api.patch<EventRegistration>(`/events/${eventId}/registrations/${regId}`, data),

  // Check-in
  checkIn: (eventId: string, contactId: string) =>
    api.post<EventRegistration>(`/events/${eventId}/check-in/${contactId}`),
  batchCheckIn: (eventId: string, contactIds: string[]) =>
    api.post<{ success: boolean; checked_in: number }>(`/events/${eventId}/check-in`, { contact_ids: contactIds }),

  // Legacy RSVP (for backward compatibility)
  getRsvps: (id: string) => api.get<{ rsvps: EventRsvp[]; summary: RsvpSummary }>(`/events/${id}/rsvps`),
  rsvp: (id: string, data: { contact_id: string; status: string; headcount?: number; notes?: string }) =>
    api.post(`/events/${id}/rsvp`, data),

  // Ceremony declarations
  getDeclarations: (eventId: string) =>
    api.get<{ declarations: CeremonyDeclaration[]; count: number }>(`/events/${eventId}/declarations`),
  processDeclarations: (eventId: string, contactIds: string[], declarationDate?: string) =>
    api.post<{ success: boolean; processed: number; already_declared: number; errors: number }>(
      `/events/${eventId}/declare`,
      { contact_ids: contactIds, declaration_date: declarationDate }
    ),
};

// Import types
export interface ImportPreview {
  file_id: string;
  headers: string[];
  sample_rows: string[][];
  total_rows: number;
  suggested_mappings: Record<string, string>;
}

export interface ImportValidation {
  valid_count: number;
  error_count: number;
  duplicate_count: number;
  errors: Array<{ row: number; field: string; message: string }>;
  duplicates: Array<{ row: number; email: string; existing_id: string }>;
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
}

// Activity types
export interface Activity {
  id: string;
  contact_id: string;
  contact_email: string;
  contact_name: string;
  activity_type: string;
  activity_source: string | null;
  activity_date: string;
  description: string | null;
  campaign_name: string | null;
  amount: number | null;
  engagement_points: number;
}

export const activitiesApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Activity>>('/activities', { params }),
};

export const importApi = {
  uploadContacts: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ImportPreview>('/import/contacts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  validateContacts: (data: { mappings: Record<string, string>; file_id: string }) =>
    api.post<ImportValidation>('/import/contacts/validate', data),
  executeContacts: (data: {
    file_id: string;
    mappings: Record<string, string>;
    options: { skip_duplicates?: boolean; update_duplicates?: boolean };
  }) => api.post<ImportResult>('/import/contacts/execute', data),
  downloadTemplate: () => api.get('/import/contacts/template', { responseType: 'blob' }),
};

// Duplicate types
export interface PotentialDuplicate {
  id: string;
  contact_id_1: string;
  contact_id_2: string;
  match_type: 'email' | 'phone' | 'name_address' | 'household' | 'external_id';
  match_score: number;
  match_details: Record<string, unknown> | null;
  status: 'pending' | 'confirmed_duplicate' | 'not_duplicate' | 'merged' | 'ignored';
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  created_at: string;
  contact1?: Contact;
  contact2?: Contact;
}

export interface DuplicateStats {
  pending: number;
  confirmed: number;
  merged: number;
  notDuplicate: number;
  ignored: number;
  byMatchType: Record<string, number>;
}

export interface MergeResult {
  success: boolean;
  merged_contact: Contact;
  archived_contact_id: string;
  summary: {
    activitiesMoved: number;
    donationsMoved: number;
    membershipsMoved: number;
    notesMoved: number;
    declarationsMoved: number;
    externalIdsMerged: string[];
  };
}

export interface DuplicatePairDetail {
  duplicate: PotentialDuplicate;
  contact1: Contact;
  contact2: Contact;
}

export const duplicatesApi = {
  list: (params?: { limit?: number; page?: number; min_score?: number; match_type?: string }) =>
    api.get<PaginatedResponse<PotentialDuplicate>>('/duplicates', { params }),
  get: (id: string) => api.get<DuplicatePairDetail>(`/duplicates/${id}`),
  stats: () => api.get<DataResponse<DuplicateStats>>('/duplicates/stats'),
  merge: (id: string, data: { primary_contact_id: string; field_selections?: Record<string, 'primary' | 'secondary'>; notes?: string }) =>
    api.post<MergeResult>(`/duplicates/${id}/merge`, data),
  markNotDuplicate: (id: string, notes?: string) =>
    api.post(`/duplicates/${id}/not-duplicate`, { notes }),
  ignore: (id: string, notes?: string) =>
    api.post(`/duplicates/${id}/ignore`, { notes }),
  scan: (matchType: 'email' | 'phone' | 'name_address', limit?: number) =>
    api.post<{ success: boolean; duplicates_found: number }>('/duplicates/scan', { match_type: matchType, limit }),
  forContact: (contactId: string) =>
    api.get<DataResponse<PotentialDuplicate[]>>(`/duplicates/contact/${contactId}`),
  directMerge: (data: { primary_contact_id: string; secondary_contact_id: string; field_selections?: Record<string, 'primary' | 'secondary'>; notes?: string }) =>
    api.post<MergeResult>('/duplicates/merge', data),
};

// ============================================
// THE COUNT API - Phase 1: 15,832 Declared Texians
// ============================================

export interface TheCountSummary {
  total_declared: number;
  goal: number;
  percentage: number;
  declared_last_7d: number;
  declared_last_30d: number;
  contacts_with_district: number;
  total_contacts: number;
  districts_with_declarations: number;
  total_districts: number;
  last_updated: string;
}

export interface DistrictCount {
  house_district: string;
  declared_count: number;
  target: number;
  percentage: number;
  total_contacts: number;
}

export interface DistrictCountResponse {
  districts: DistrictCount[];
  summary: {
    total_declared: number;
    districts_with_declarations: number;
    total_districts: number;
    target_per_district: number;
    average_per_district: number;
  };
}

export interface RecruitmentLeader {
  referrer_id: string;
  first_name: string | null;
  last_name: string | null;
  county: string | null;
  house_district: string | null;
  recruited_count: number;
}

export interface DeclarationGrowth {
  date: string;
  daily_count: number;
  cumulative_count: number;
}

export interface CountyDistrictBreakdown {
  county: string;
  total_declared: number;
  districts_in_county: number;
  district_breakdown: Array<{
    house_district: string;
    declared_count: number;
    total_contacts: number;
    percentage: number;
  }>;
}

export interface DistrictContactsResponse {
  district: string;
  contacts: Array<{
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    county: string | null;
    declaration_status: string;
    declaration_date: string | null;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface DeclareContactResponse {
  contact: {
    id: string;
    declaration_status: string;
    declaration_date: string;
    house_district: string | null;
  };
  the_count: {
    total_declared: number;
    goal: number;
    percentage: number;
  };
}

export interface DataQualityStats {
  total_contacts: number;
  with_house_district: number;
  with_senate_district: number;
  with_geocoding: number;
  without_address: number;
  coverage_percentage: number;
}

export const theCountApi = {
  // Get The Count summary
  summary: () => api.get<TheCountSummary>('/dashboard/the-count'),
  
  // Get declaration growth over time
  growth: (days?: number) => 
    api.get<DeclarationGrowth[]>('/dashboard/the-count/growth', { params: { days } }),
  
  // Get counts by House District
  byDistrict: () => 
    api.get<DistrictCountResponse>('/dashboard/the-count/by-district'),
  
  // Get recruitment leaderboard
  leaderboard: (limit?: number) => 
    api.get<RecruitmentLeader[]>('/dashboard/the-count/leaderboard', { params: { limit } }),
  
  // Get data quality stats (admin)
  dataQuality: () => 
    api.get<DataQualityStats>('/dashboard/the-count/data-quality'),
  
  // Get county-specific stats with district breakdown
  county: (countyName: string) => 
    api.get<CountyDistrictBreakdown>(`/dashboard/county/${encodeURIComponent(countyName)}`),
  
  // Export county declared texians (CSV or JSON)
  exportCounty: (countyName: string, format?: 'csv' | 'json') =>
    api.get(`/dashboard/county/${encodeURIComponent(countyName)}/export`, { 
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    }),
  
  // Get contacts by district
  contactsByDistrict: (district: string, params?: { page?: number; limit?: number; declared_only?: boolean }) =>
    api.get<DistrictContactsResponse>(`/dashboard/contacts/by-district/${district}`, { params }),
  
  // Mark contact as declared
  declareContact: (contactId: string, data?: { declaration_method?: string; referrer_id?: string }) =>
    api.patch<DeclareContactResponse>(`/dashboard/contacts/${contactId}/declare`, data || {}),
  
  // Export district declared texians
  exportDistrict: (district: string, format?: 'csv' | 'json') =>
    api.get(`/dashboard/district/${district}/export`, { 
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    }),
};

// ============================================
// JOURNEY TYPES & API (Phase 2)
// ============================================

export type JourneyStage = 
  | 'stranger'
  | 'registered_supporter'
  | 'email_subscriber'
  | 'engaged_subscriber'
  | 'paying_member'
  | 'declared_texian'
  | 'active_texian'
  | 'county_leader';

export type EngagementLevel = 'cold' | 'warm' | 'hot';

export type MissionType = 
  | 'share_declaration'
  | 'identify_recruit'
  | 'contact_rep'
  | 'connect_locally'
  | 'monthly_action';

export type MissionStatus = 'assigned' | 'completed' | 'skipped';

// Journey stage display config
export const JOURNEY_STAGE_CONFIG: Record<JourneyStage, { 
  label: string; 
  color: string; 
  bgColor: string;
  description: string;
  order: number;
}> = {
  stranger: { 
    label: 'Stranger', 
    color: 'text-gray-600', 
    bgColor: 'bg-gray-100',
    description: 'Not yet in contact with TNM',
    order: 1,
  },
  registered_supporter: { 
    label: 'Registered Supporter', 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-100',
    description: 'Has registered but not opted into email',
    order: 2,
  },
  email_subscriber: { 
    label: 'Email Subscriber', 
    color: 'text-cyan-600', 
    bgColor: 'bg-cyan-100',
    description: 'Subscribed to email communications',
    order: 3,
  },
  engaged_subscriber: { 
    label: 'Engaged Subscriber', 
    color: 'text-green-600', 
    bgColor: 'bg-green-100',
    description: 'Active email engagement in last 30 days',
    order: 4,
  },
  paying_member: { 
    label: 'Paying Member', 
    color: 'text-emerald-600', 
    bgColor: 'bg-emerald-100',
    description: 'Active TNM membership',
    order: 5,
  },
  declared_texian: { 
    label: 'Declared Texian', 
    color: 'text-amber-600', 
    bgColor: 'bg-amber-100',
    description: 'Completed the Texian Declaration',
    order: 6,
  },
  active_texian: { 
    label: 'Active Texian', 
    color: 'text-orange-600', 
    bgColor: 'bg-orange-100',
    description: 'Declared + completed mission this month',
    order: 7,
  },
  county_leader: { 
    label: 'County Leader', 
    color: 'text-red-600', 
    bgColor: 'bg-red-100',
    description: 'Has a county leadership role',
    order: 8,
  },
};

// Engagement level config
export const ENGAGEMENT_LEVEL_CONFIG: Record<EngagementLevel, { 
  label: string; 
  color: string; 
  bgColor: string;
  min: number;
  max: number;
}> = {
  cold: { label: 'Cold', color: 'text-blue-700', bgColor: 'bg-blue-100', min: 0, max: 33 },
  warm: { label: 'Warm', color: 'text-yellow-700', bgColor: 'bg-yellow-100', min: 34, max: 66 },
  hot: { label: 'Hot', color: 'text-red-700', bgColor: 'bg-red-100', min: 67, max: 100 },
};

// Mission type config
export const MISSION_TYPE_CONFIG: Record<MissionType, {
  label: string;
  description: string;
  week: number | null;
  icon: string;
}> = {
  share_declaration: {
    label: 'Share Your Declaration',
    description: 'Share your declaration on social media, email friends, or tell someone in person',
    week: 1,
    icon: '📢',
  },
  identify_recruit: {
    label: 'Identify First Recruit',
    description: 'Identify at least one person who might be interested in declaring',
    week: 2,
    icon: '🎯',
  },
  contact_rep: {
    label: 'Contact Your Representative',
    description: 'Contact your state representative about the TEXIT referendum',
    week: 3,
    icon: '📞',
  },
  connect_locally: {
    label: 'Connect Locally',
    description: 'Attend a county meeting or connect with your County Coordinator',
    week: 4,
    icon: '🤝',
  },
  monthly_action: {
    label: 'Monthly Action',
    description: 'Complete this month\'s mobilization action',
    week: null,
    icon: '⚡',
  },
};

export interface ContactMission {
  id: string;
  contact_id: string;
  mission_type: MissionType;
  mission_week: number | null;
  status: MissionStatus;
  assigned_at: string;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface JourneyHistory {
  id: string;
  contact_id: string;
  from_stage: JourneyStage | null;
  to_stage: JourneyStage;
  changed_at: string;
  changed_by: string | null;
  change_reason: string | null;
  auto_detected: boolean;
}

export interface ContactJourneyData {
  contact_id: string;
  journey_stage: JourneyStage;
  journey_stage_updated_at: string | null;
  engagement_score: number;
  engagement_level: EngagementLevel;
  activation_status: boolean;
  referral_code: string | null;
  referrer_id: string | null;
  referral_count: number;
  history: JourneyHistory[];
  missions: ContactMission[];
  referrer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
  recruits: Array<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    declaration_date: string | null;
  }>;
}

export interface JourneyFunnelItem {
  journey_stage: JourneyStage;
  count: number;
  percentage: number;
}

export interface JourneyFunnelData {
  stages: JourneyFunnelItem[];
  conversions: Array<{
    from_stage: JourneyStage;
    to_stage: JourneyStage;
    conversion_rate: number;
  }>;
}

export interface ActivationRateData {
  total_declared: number;
  active_this_month: number;
  activation_rate: number;
  monthly_history: Array<{
    month: string;
    contacts_with_missions: number;
    total_declared: number;
    activation_rate: number;
  }>;
}

export interface ReferralLeaderboardItem {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  referral_code: string;
  referral_count: number;
  county: string | null;
  journey_stage: JourneyStage;
}

export interface MissionStats {
  total_assigned: number;
  total_completed: number;
  total_skipped: number;
  completion_rate: number;
  by_type: Array<{
    mission_type: MissionType;
    assigned: number;
    completed: number;
    skipped: number;
    completion_rate: number;
  }>;
  by_week: Array<{
    mission_week: number;
    assigned: number;
    completed: number;
    completion_rate: number;
  }>;
}

export interface EngagementDistribution {
  hot: number;
  warm: number;
  cold: number;
  total: number;
}

// Journey API
export const journeyApi = {
  // Get journey data for a contact
  getJourney: (contactId: string) =>
    api.get<ContactJourneyData>(`/contacts/${contactId}/journey`),

  // Update journey stage manually
  updateStage: (contactId: string, stage: JourneyStage, reason?: string) =>
    api.put<ContactJourneyData>(`/contacts/${contactId}/journey/stage`, { stage, reason }),

  // Recalculate journey stage
  recalculate: (contactId: string) =>
    api.post<ContactJourneyData>(`/contacts/${contactId}/journey/recalculate`),

  // Get missions for a contact
  getMissions: (contactId: string) =>
    api.get<{ data: ContactMission[] }>(`/contacts/${contactId}/missions`),

  // Assign a mission
  assignMission: (contactId: string, data: { mission_type: MissionType; mission_week?: number; notes?: string }) =>
    api.post<ContactMission>(`/contacts/${contactId}/missions`, data),

  // Assign onboarding missions
  assignOnboarding: (contactId: string) =>
    api.post<{ data: ContactMission[] }>(`/contacts/${contactId}/missions/onboarding`),

  // Update mission status
  updateMission: (contactId: string, missionId: string, data: { status: 'completed' | 'skipped'; notes?: string }) =>
    api.patch<ContactMission>(`/contacts/${contactId}/missions/${missionId}`, data),

  // Get referral network
  getReferrals: (contactId: string) =>
    api.get<{
      contact: { id: string; first_name: string | null; last_name: string | null; email: string; referral_code: string | null };
      referrer: { id: string; first_name: string | null; last_name: string | null; email: string } | null;
      recruits: Array<{ id: string; first_name: string | null; last_name: string | null; email: string; referral_count: number; created_at: string }>;
    }>(`/contacts/${contactId}/referrals`),

  // Track a referral
  trackReferral: (contactId: string, referralCode: string) =>
    api.post<{ success: boolean; message: string }>('/referrals/track', { contact_id: contactId, referral_code: referralCode }),

  // Dashboard endpoints
  journeyFunnel: () =>
    api.get<JourneyFunnelData>('/dashboard/journey-funnel'),

  activationRate: () =>
    api.get<ActivationRateData>('/dashboard/activation-rate'),

  referralLeaderboard: (limit?: number) =>
    api.get<{ data: ReferralLeaderboardItem[] }>('/dashboard/referral-leaderboard', { params: { limit } }),

  missionStats: () =>
    api.get<MissionStats>('/dashboard/mission-stats'),

  engagementDistribution: () =>
    api.get<EngagementDistribution>('/dashboard/engagement-distribution'),
};
